import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetUpYourDomain = () => {
  const [activeSection, setActiveSection] = useState("domain-type");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "domain-type",
        "connect-domain",
        "ssl-security",
        "test-connection",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Your Domain – xPage Help Center"
        description="Learn how to set up a domain for your xPage store. Connect a free subdomain or your own custom domain, enable SSL, and go live quickly."
        keywords="xPage domain setup, connect custom domain, free subdomain, SSL, online store, xPage help center"
        canonical="https://help.xpage.ai/help/quick-launch-checklist/Set%20Up%20Your%20Domain"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Set Up Your Domain
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Connecting a domain gives your store a professional edge and builds
          trust with customers. With xPage, you can either use a free subdomain
          or connect your own custom domain for a fully branded experience.
        </p>

        {/* Choose Domain Type Section */}
        <section id="domain-type" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Choose your domain type
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            xPage offers two options for publishing your landing page:
          </p>

          {/* Free Subdomain Card */}
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mb-4">
            <h3 className="text-lg font-semibold text-systemTitle mb-2">
              Free xPage Subdomain
            </h3>
            <p className="text-sm text-systemLabel mb-3">
              If you don't have a domain yet, you can launch instantly using a
              free subdomain like:
            </p>
            <div className="bg-white border border-systemBorder rounded px-4 py-3 inline-block">
              <span className="text-sm font-medium text-core">
                myxpage.shop
              </span>
            </div>
          </div>

          {/* Custom Domain Card */}
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <h3 className="text-lg font-semibold text-systemTitle mb-2">
              Custom Domain
            </h3>
            <p className="text-sm text-systemLabel">
              For a more branded and trustworthy appearance, you can connect a
              domain you already own, such as:
            </p>
            <div className="bg-white border border-systemBorder rounded px-4 py-3 inline-block mt-3">
              <span className="text-sm font-medium text-core">
                yourstore.com
              </span>
            </div>
          </div>
        </section>

        {/* How to Connect Custom Domain */}
        <section id="connect-domain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to connect a custom domain
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            To use a custom domain, follow these steps:
          </p>

          <ol className="list-decimal list-inside space-y-1 mb-4">
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Go to the Settings tab</span> in
              your xPage dashboard.
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Click on Domain Settings.</span>
              <br />
              To connect a domain to your hosting, add these records to your
              DNS:
              <div className="bg-systemBackground border border-systemBorder rounded p-4 mt-2 space-y-2">
                <div className="text-sm font-medium text-core">
                  15.197.94.0 as an 'A' record
                </div>
                <div className="text-sm font-medium text-core">
                  15.197.169.74 as an 'A' record
                </div>
              </div>
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Then insert your domain name
              </span>{" "}
              in the field below and click 'Connect' (e.g., www.yourstore.com)
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Update your DNS records</span>{" "}
              with your domain registrar:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li className="text-sm text-systemLabel">
                  A Record pointing to xPage's IP address (provided in the
                  dashboard)
                </li>
                <li className="text-sm text-systemLabel">
                  CNAME for subdomains (if needed)
                </li>
              </ul>
            </li>
          </ol>

          <div className="bg-system200 border-l-4 border-system400 px-4 py-3">
            <p className="text-sm text-systemLabel">
              <span className="font-semibold">Note:</span> DNS updates can take
              up to 24 to 48 hours to fully propagate. xPage will monitor the
              status and let you know once your domain is active.
            </p>
          </div>
        </section>

        {/* SSL & Security Section */}
        <section id="ssl-security" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            SSL & Security
          </h2>

          <p className="text-sm text-systemLabel mt-2">
            Once your domain is connected, xPage automatically issues an SSL
            certificate for secure browsing (HTTPS). No technical setup is
            required.
          </p>
        </section>

        {/* Test Connection Section */}
        <section id="test-connection" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Test your connection
          </h2>

          <p className="text-sm text-systemLabel mt-2">
            After updating your DNS, return to xPage and click Verify Domain.
            Once verified, your store will be live on your chosen domain, ready
            for customers around the world.
          </p>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#domain-type"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "domain-type"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Domain Type
              </a>
            </li>
            <li>
              <a
                href="#connect-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "connect-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Connect Domain
              </a>
            </li>
            <li>
              <a
                href="#ssl-security"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "ssl-security"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                SSL & Security
              </a>
            </li>
            <li>
              <a
                href="#test-connection"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "test-connection"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Test Connection
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetUpYourDomain;
